<template>
	<div
		:class="classes"
		:style="style"
		ref="overlay"
	>
		<slot></slot>
	</div>
</template>

<script>
import {
	HorizontalOrientationPreference,
	VerticalOrientationPreference
} from "@teaming/common-enums";

import { calculatePosition } from "./positionHelper";

import ViewportMixin from "../../mixins/ViewportMixin";

export default {
	name: "FixedPositionOverlay",
	mixins: [ ViewportMixin, ],
	data() {
		return {
			overlayHeight: 0,
			overlayWidth: 0,
			style: `position:fixed;top:0;left:0;z-index:${ this.zIndex };`,
		};
	},
	props: {
		className: {
			type: String,
			default: "",
		},
		horizontalOrientation: {
			type: String,
			default: HorizontalOrientationPreference.LEFT,
			validator( type ) {
				return HorizontalOrientationPreference.includes( type );
			},
		},
		isVisible: {
			type: Boolean,
			default: false,
		},
		left: {
			type: Number,
			default: 0,
		},
		top: {
			type: Number,
			default: 0,
		},
		verticalOrientation: {
			type: String,
			default: VerticalOrientationPreference.BOTTOM,
			validator( type ) {
				return VerticalOrientationPreference.includes( type );
			},
		},
		zIndex: {
			type: Number,
			default: 999,
		},
	},
	computed: {
		classes() {
			const classes = {
				"fixed-position-overlay": true,
				"fixed-position-overlay--visible": this.isVisible,
				...this.className.split( " " ).reduce( ( acc, c ) => {
					acc[ c ] = true;
					return acc;
				}, {} ),
			};

			return classes;
		},
		coordinates() {
			return { x: this.left, y: this.top, };
		},
		overlay() {
			return {
				width: this.overlayWidth,
				height: this.overlayHeight,
			};
		},
		viewport() {
			return {
				width: this.viewportWidth,
				height: this.viewportHeight,
			};
		},
	},
	watch: {
		isVisible() {
			this.calculateStyle();
		},
		top() {
			this.calculateStyle();
		},
		left() {
			this.calculateStyle();
		},
		overlay() {
			this.calculateStyle();
		},
		viewport() {
			this.calculateStyle();
		},
	},
	methods: {
		calculateStyle() {
			const {
				coordinates,
				overlay,
				viewport,
				horizontalOrientation,
				verticalOrientation,
			} = this;

			// NOTE: fixed position calculations will be wrong if an ancestor
			// element has a CSS transform applied. See: https://stackoverflow.com/a/49189336/223147
			const { x, y, } = calculatePosition( {
				coordinates,
				overlay,
				viewport,
				horizontalOrientation,
				verticalOrientation,
			} );

			this.style = `position:fixed;top:${ y }px;left:${ x }px;z-index:${ this.zIndex };`;
		},
	},
	mounted() {
		this.resizeObserver = new ResizeObserver( () => {
			this.overlayWidth = this.$refs.overlay?.clientWidth || 0;
			this.overlayHeight = this.$refs.overlay?.clientHeight || 0;
		} );
		this.resizeObserver.observe( this.$refs.overlay );
	},
};
</script>

<style lang="scss" scoped>
.fixed-position-overlay {
	visibility: hidden;
}

.fixed-position-overlay--visible {
	visibility: visible;
}
</style>
